/**
 * PATH: packages/email-hub/src/feature/components/JunkButton.tsx
 * FILE_ID: EMAILHUB-JUNKBUTTON-001
 * PURPOSE: The universal "Junk" verb for the email reader header — a
 *          split control: the main face bins THIS email now (quick,
 *          reversible from Trash); the caret opens "Junk options" that
 *          arm an auto-junk rule (auto-trash this sender / whole
 *          domain), which RETROACTIVELY bins existing matches AND
 *          PROSPECTIVELY bins future arrivals.
 *
 * WHY:     Operator (2026-06-14): a marketing blast (e.g. an Amazon
 *          promo) the AI auto-filed onto "My Inbox · Triage" surfaced
 *          only "Open card / Done" — Junk was unreachable from the
 *          header once an email was on a board. Junk must be a
 *          first-class, always-available disposition with retro +
 *          prospective options, mirroring the right-click
 *          "Junk & auto-trash" submenu so behaviour is identical
 *          everywhere.
 *
 * FLOW:
 *   ┌───────────────┐   ┌────────────────────┐   ┌────────────────┐
 *   │ click main    │ → │ bulkAction(trash)  │ → │ onRefetch()    │
 *   │ click ▾ → opt │ → │ createAutoJunkRule │ → │ toast + refetch│
 *   └───────────────┘   └────────────────────┘   └────────────────┘
 *
 * DEPENDENCIES:
 *   - ../hooks/useEmails (useEmailActions): bulkAction + createAutoJunkRule
 *   - ../lib/junkTargets (deriveJunkTargets): pure address → match values
 *   - ../../_internal/components/Toast: feedback
 *
 * RELATED:
 *   - ./SmartActionBar.tsx: renders this for the `mark_junk` verb
 *   - ./EmailContextMenu.tsx: the right-click "Junk & auto-trash" twin
 *   - ../lib/headerActions.ts: emits the `mark_junk` kind
 */

import { useCallback, useRef, useState, type MouseEvent } from 'react'
import { AtSign, Ban, ChevronDown, Globe, Loader2 } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import { type Email } from '../hooks/useEmails'
import { useJunkActions } from '../hooks/useJunkActions'
import type { MailboxMenuItem } from '../lib/mailboxMenuItems'
import { MailboxContextMenu } from './MailboxContextMenu'

interface JunkButtonProps {
  email: Pick<Email, 'id' | 'from_address'>
  /** Refetch the list + open email after the disposition mutates. */
  onRefetch?: () => void
  /**
   * Operator (2026-06-17): junk is terminal, so the reader should drop the
   * email + advance to the next message. When provided, the host owns that
   * remove+advance; omitted → fall back to `onRefetch`.
   */
  onResolved?: (emailId: string) => void
  /** Disable while a sibling header action is running. */
  disabled?: boolean
  /** Filled (primary) vs outline (secondary) treatment. */
  variant?: 'primary' | 'secondary'
}

export function JunkButton({
  email,
  onRefetch,
  onResolved,
  disabled = false,
  variant = 'secondary',
}: JunkButtonProps) {
  const { busy, junkNow: junkNowRaw, armRule: armRuleRaw, exact, domain } =
    useJunkActions(email, onRefetch, onResolved)
  // The options menu renders through MailboxContextMenu — a body PORTAL that
  // measures itself and clamps to the viewport (min-w-[200px] max-w-[260px]).
  // The previous bespoke `absolute right-0 min-w-[14rem]` dropdown was neither
  // portaled nor clamped, so on a phone-width WebView it spilled past / was
  // clipped by the flex-wrap reader toolbar — operator (2026-06-20): "the menu
  // drop down didn't properly size to the app." This matches CardJunkControl.
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null)
  const caretRef = useRef<HTMLButtonElement>(null)

  const openMenu = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (busy || disabled) return
    if (menuAt) {
      setMenuAt(null)
      return
    }
    const rect = caretRef.current?.getBoundingClientRect()
    if (rect) setMenuAt({ x: rect.left, y: rect.bottom + 4 })
  }

  const junkNow = useCallback(async () => {
    if (disabled) return
    await junkNowRaw()
  }, [disabled, junkNowRaw])

  // MailboxContextMenu fires the item's onClick then self-closes, so arming a
  // rule needn't touch menu state here.
  const armRule = useCallback(
    async (matchType: 'sender_domain' | 'sender_exact') => {
      if (disabled) return
      await armRuleRaw(matchType)
    },
    [disabled, armRuleRaw],
  )

  const faceTone =
    variant === 'primary'
      ? 'bg-rose-600 text-white hover:bg-rose-700 border-rose-600'
      : 'border-border bg-background text-foreground/80 hover:bg-accent'
  const hasOptions = Boolean(exact || domain)

  // The caret menu offers ONLY the rule options (the main face already bins
  // now). Labels mirror CardJunkControl so junk reads identically everywhere.
  const optionItems: MailboxMenuItem[] = []
  if (domain) {
    optionItems.push({
      id: 'junk-domain',
      label: `Auto-trash domain · ${domain}`,
      icon: Globe,
      danger: true,
      onClick: () => void armRule('sender_domain'),
    })
  }
  if (exact) {
    optionItems.push({
      id: 'junk-exact',
      label: `Just this sender · ${exact}`,
      icon: AtSign,
      danger: true,
      onClick: () => void armRule('sender_exact'),
    })
  }

  return (
    <div className="relative inline-flex items-center">
      <button
        type="button"
        onClick={() => void junkNow()}
        disabled={busy || disabled}
        title="Junk - bin this email now"
        data-testid="smart-action-mark_junk"
        className={cn(
          'inline-flex items-center gap-1 border px-2.5 py-1 text-[11px] font-medium touch-manipulation',
          'transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          hasOptions ? 'rounded-l-full' : 'rounded-full',
          faceTone,
          (busy || disabled) && 'opacity-60 cursor-not-allowed',
        )}
      >
        {busy ? (
          <Loader2 className="h-3 w-3 animate-spin shrink-0" aria-hidden />
        ) : (
          <Ban className="h-3 w-3 shrink-0" aria-hidden />
        )}
        {/* Verb, not noun — matches the inbox card so the disposition reads
            as an ACTION everywhere, never as an applied "Junk" label. */}
        <span>Mark junk</span>
      </button>
      {hasOptions && (
        <button
          ref={caretRef}
          type="button"
          onClick={openMenu}
          disabled={busy || disabled}
          title="Junk options - auto-trash this sender or domain"
          aria-haspopup="menu"
          aria-expanded={menuAt !== null}
          data-testid="smart-action-mark_junk-options"
          className={cn(
            'inline-flex items-center justify-center border border-l-0 px-1 py-1 rounded-r-full touch-manipulation',
            'transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            faceTone,
            (busy || disabled) && 'opacity-60 cursor-not-allowed',
          )}
        >
          <ChevronDown className="h-3 w-3" aria-hidden />
        </button>
      )}
      {menuAt && hasOptions && (
        <MailboxContextMenu
          x={menuAt.x}
          y={menuAt.y}
          items={optionItems}
          ariaLabel="Junk options"
          onClose={() => setMenuAt(null)}
        />
      )}
    </div>
  )
}

export default JunkButton
